// Client-side CSV export for the Scorecards tab's Week/Month tables
// (2026-10-06, Patrick's request -- same "export to Excel," built as CSV
// pattern every other export in this codebase uses, see ahtByRepExport.js's
// own header comment for why CSV). Built directly from the already-pooled
// `{ agentRows, total, hasSupportEmail }` ReportingPage.jsx computes via
// poolScorecardRows()/poolScorecardTotal(), so the export always exactly
// matches whatever periods/team/agents are currently selected on screen --
// there is no separate server round trip or re-pooling here.

function csvEscape(value) {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (/["\n\r,]/.test(str)) return `"${str.replace(/"/g, '""')}"`
  return str
}

// ms -> "H:MM:SS" or "M:SS", matching ReportingPage.jsx's own formatHandleMs()
// -- duplicated rather than imported (that function isn't exported, and
// it's small), same precedent ahtByRepExport.js's own formatAhtMs already
// set. Empty string for null, never a dash -- an empty CSV cell is this
// codebase's own null-handling convention for every exported file.
function formatAhtMs(ms) {
  if (typeof ms !== 'number' || !isFinite(ms)) return ''
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

// A fraction (0-1) -> "98.43", matching formatPercent()'s own on-screen
// precision, minus the '%' sign (Excel's own Percentage format expects a
// bare number, and a raw CSV has no way to carry formatting anyway). Empty
// for null, same reasoning as formatAhtMs above. Shared by ADH % and
// Utilization % -- both are plain fractions, same formatting rule.
function formatPercentForCsv(fraction) {
  if (typeof fraction !== 'number' || !isFinite(fraction)) return ''
  return (fraction * 100).toFixed(2)
}

// Minutes -> "38.5", matching ReportingPage.jsx's own formatMinutesAsHours()
// -- same duplication precedent as formatAhtMs above.
function formatCxhubScheduledHoursForCsv(minutes) {
  if (typeof minutes !== 'number' || !isFinite(minutes)) return ''
  return (minutes / 60).toFixed(1)
}

function agentRowToCsvRow(row, hasSupportEmail, hasBpEmail) {
  return [
    row.agent,
    row.totalContacts,
    formatAhtMs(row.emailAht),
    ...(hasSupportEmail ? [row.supportEmailContacts > 0 ? formatAhtMs(row.supportEmailAht) : ''] : []),
    ...(hasBpEmail ? [
      row.bpEmails > 0 ? row.bpEmails : '',
      row.bpEmails > 0 ? formatAhtMs(row.bpEmailAht) : '',
      row.bpEmails > 0 ? formatAhtMs(row.combinedEmailAht) : '',
    ] : []),
    formatPercentForCsv(row.adh),
    formatCxhubScheduledHoursForCsv(row.cxhubScheduledMinutes),
    formatPercentForCsv(row.utilization),
  ]
}

// agentRows/total: the pooled result ReportingPage.jsx's scorecardRows()
// already computed for whatever's on screen. hasSupportEmail: whether the
// Support Email AHT column is currently shown (same condition the table
// itself uses) -- the export's columns must always match the visible table.
// hasBpEmail: the same for the three Bright Pattern columns.
export function buildScorecardsCsv({ agentRows, total, hasSupportEmail, hasBpEmail = false }) {
  const headers = [
    'Agent', 'Total Emails', 'Email AHT', ...(hasSupportEmail ? ['Support Email AHT'] : []),
    ...(hasBpEmail ? ['BP Emails', 'BP Email AHT', 'Combined Email AHT'] : []),
    'ADH %', 'Scheduled Hours', 'Utilization %',
  ]
  const lines = [headers.map(csvEscape).join(',')]

  for (const row of agentRows) lines.push(agentRowToCsvRow(row, hasSupportEmail, hasBpEmail).map(csvEscape).join(','))
  if (total) lines.push(agentRowToCsvRow({ ...total, agent: 'Total' }, hasSupportEmail, hasBpEmail).map(csvEscape).join(','))

  // CRLF (RFC 4180), same convention every other export in this codebase uses.
  return lines.join('\r\n') + '\r\n'
}
